export interface NicheAgentTemplate {
  id: string;
  name: string;
  description: string;
  agentId: string;
  prompt: string;
  tags: string[];
}

export const NICHE_AGENT_TEMPLATES: NicheAgentTemplate[] = [
  // Architecture Templates
  {
    id: 'arch-modern-home',
    name: 'Modern Sustainable Home',
    description: 'Design a modern eco-friendly residential building',
    agentId: 'architecture',
    prompt: 'Design a modern sustainable single-family home with passive solar features, green roof, and natural materials. 2,500 sq ft on a sloped lot with southern exposure.',
    tags: ['residential', 'sustainable', 'modern']
  },
  {
    id: 'arch-office-tower',
    name: 'Mixed-Use Office Tower',
    description: 'Create a contemporary mixed-use urban development',
    agentId: 'architecture',
    prompt: 'Design a 30-story mixed-use tower with retail ground floor, office spaces, and rooftop amenities. Focus on energy efficiency and urban integration.',
    tags: ['commercial', 'urban', 'mixed-use']
  },

  // Culinary Templates
  {
    id: 'culinary-fusion',
    name: 'Asian-Mediterranean Fusion',
    description: 'Create innovative fusion cuisine dishes',
    agentId: 'culinary',
    prompt: 'Create a tasting menu combining Japanese techniques with Mediterranean ingredients. Focus on umami, seasonal vegetables, and elegant presentation.',
    tags: ['fusion', 'fine-dining', 'innovative']
  },
  {
    id: 'culinary-molecular',
    name: 'Molecular Gastronomy Experience',
    description: 'Design cutting-edge molecular dishes',
    agentId: 'culinary',
    prompt: 'Design a molecular gastronomy dessert using spherification, foams, and temperature contrasts. Theme: deconstructed tropical fruits.',
    tags: ['molecular', 'dessert', 'innovative']
  },

  // Environmental Templates
  {
    id: 'env-rainforest',
    name: 'Tropical Rainforest Ecosystem',
    description: 'Visualize a dense rainforest environment',
    agentId: 'environment',
    prompt: 'Create a lush tropical rainforest scene with multiple canopy layers, diverse flora, morning mist, and filtered sunlight through the trees.',
    tags: ['nature', 'tropical', 'ecosystem']
  },
  {
    id: 'env-arctic',
    name: 'Arctic Tundra Landscape',
    description: 'Design a stark arctic environment',
    agentId: 'environment',
    prompt: 'Visualize an arctic tundra during spring thaw with melting ice, emerging vegetation, aurora borealis in the sky, and wildlife adaptations.',
    tags: ['arctic', 'climate', 'landscape']
  },

  // Mechanical Templates
  {
    id: 'mech-robotics',
    name: 'Advanced Robotics System',
    description: 'Design a sophisticated robotic mechanism',
    agentId: 'mechanical',
    prompt: 'Design a humanoid robot arm with 7 degrees of freedom, precision grippers, and force feedback sensors for delicate assembly tasks.',
    tags: ['robotics', 'automation', 'precision']
  },
  {
    id: 'mech-engine',
    name: 'High-Performance Engine',
    description: 'Create an innovative engine design',
    agentId: 'mechanical',
    prompt: 'Design a hybrid turbocharged engine with variable valve timing, direct injection, and integrated electric motor for performance vehicles.',
    tags: ['automotive', 'performance', 'hybrid']
  },

  // Soundscape Templates
  {
    id: 'sound-nature',
    name: 'Forest Dawn Soundscape',
    description: 'Create an immersive natural audio environment',
    agentId: 'soundscape',
    prompt: 'Compose a dawn forest soundscape with bird songs, rustling leaves, distant stream, and gradual transition from night to morning sounds.',
    tags: ['nature', 'ambient', 'relaxation']
  },
  {
    id: 'sound-scifi',
    name: 'Futuristic City Ambience',
    description: 'Design a sci-fi urban sound environment',
    agentId: 'soundscape',
    prompt: 'Create a futuristic cityscape audio with hovering vehicles, holographic advertisements, AI announcements, and cyberpunk atmosphere.',
    tags: ['sci-fi', 'urban', 'futuristic']
  },

  // Textile Templates
  {
    id: 'textile-smart',
    name: 'Smart Performance Fabric',
    description: 'Design high-tech athletic textiles',
    agentId: 'textile',
    prompt: 'Design a smart athletic fabric with moisture-wicking, temperature regulation, compression zones, and embedded biometric sensors.',
    tags: ['technical', 'athletic', 'smart']
  },
  {
    id: 'textile-luxury',
    name: 'Luxury Evening Wear Fabric',
    description: 'Create elegant formal textiles',
    agentId: 'textile',
    prompt: 'Design a luxury evening gown fabric with silk base, metallic thread embroidery, color-shifting properties, and fluid draping qualities.',
    tags: ['luxury', 'fashion', 'formal']
  },

  // Botanical Templates
  {
    id: 'botanical-rare',
    name: 'Rare Orchid Species',
    description: 'Illustrate exotic orchid varieties',
    agentId: 'botanical',
    prompt: 'Create a detailed botanical illustration of a rare orchid species showing flower structure, root system, leaves, and growth stages.',
    tags: ['scientific', 'orchid', 'detailed']
  },
  {
    id: 'botanical-medicinal',
    name: 'Medicinal Herb Garden',
    description: 'Design a therapeutic plant collection',
    agentId: 'botanical',
    prompt: 'Illustrate a medicinal herb garden layout with 20 therapeutic plants, showing their healing properties and optimal growing conditions.',
    tags: ['medicinal', 'garden', 'therapeutic']
  },

  // Marine Templates
  {
    id: 'marine-reef',
    name: 'Coral Reef Ecosystem',
    description: 'Visualize a vibrant reef community',
    agentId: 'marine',
    prompt: 'Create a thriving coral reef scene with diverse coral species, tropical fish, cleaning stations, and bioluminescent organisms at dusk.',
    tags: ['coral', 'ecosystem', 'tropical']
  },
  {
    id: 'marine-deep',
    name: 'Deep Sea Exploration',
    description: 'Explore abyssal ocean depths',
    agentId: 'marine',
    prompt: 'Visualize a deep sea hydrothermal vent ecosystem with tube worms, bioluminescent creatures, and extreme pressure adaptations.',
    tags: ['deep-sea', 'extreme', 'bioluminescence']
  },

  // Astronomical Templates
  {
    id: 'astro-nebula',
    name: 'Stellar Nursery Nebula',
    description: 'Visualize star formation regions',
    agentId: 'astronomical',
    prompt: 'Create a stellar nursery nebula showing protostars, gas pillars, shock waves, and multiple wavelength observations from infrared to X-ray.',
    tags: ['nebula', 'star-formation', 'cosmic']
  },
  {
    id: 'astro-exoplanet',
    name: 'Habitable Exoplanet System',
    description: 'Design an alien solar system',
    agentId: 'astronomical',
    prompt: 'Design a habitable exoplanet system with binary stars, multiple moons, ring system, and potential biosignatures in the atmosphere.',
    tags: ['exoplanet', 'habitability', 'alien']
  },

  // Medical Templates
  {
    id: 'medical-surgery',
    name: 'Minimally Invasive Surgery',
    description: 'Illustrate modern surgical techniques',
    agentId: 'medical',
    prompt: 'Illustrate a laparoscopic surgery procedure showing instrument placement, internal anatomy, and step-by-step surgical approach.',
    tags: ['surgery', 'minimally-invasive', 'procedural']
  },
  {
    id: 'medical-cellular',
    name: 'Cellular Interaction',
    description: 'Visualize microscopic biological processes',
    agentId: 'medical',
    prompt: 'Visualize T-cell immune response to viral infection showing cellular recognition, activation, and antibody production at molecular level.',
    tags: ['immunology', 'cellular', 'molecular']
  },

  // Quantum Physics Templates
  {
    id: 'quantum-computing',
    name: 'Quantum Computer Architecture',
    description: 'Design quantum computing systems',
    agentId: 'quantumphysics',
    prompt: 'Visualize a quantum computer architecture with qubit arrays, entanglement networks, error correction, and cryogenic cooling systems.',
    tags: ['quantum-computing', 'technology', 'advanced']
  },
  {
    id: 'quantum-particle',
    name: 'Particle Collision Event',
    description: 'Illustrate high-energy physics',
    agentId: 'quantumphysics',
    prompt: 'Visualize a particle collision in the Large Hadron Collider showing decay products, energy traces, and Higgs boson signature.',
    tags: ['particle-physics', 'collision', 'high-energy']
  },

  // Urban Planning Templates
  {
    id: 'urban-sustainable',
    name: 'Sustainable Smart City',
    description: 'Design future urban environments',
    agentId: 'urban',
    prompt: 'Design a sustainable smart city district with green corridors, mixed-use development, public transit hubs, and renewable energy integration.',
    tags: ['sustainable', 'smart-city', 'future']
  },
  {
    id: 'urban-waterfront',
    name: 'Waterfront Revitalization',
    description: 'Transform industrial waterfront areas',
    agentId: 'urban',
    prompt: 'Plan waterfront revitalization converting industrial docks to mixed-use development with parks, boardwalks, cultural venues, and marina.',
    tags: ['waterfront', 'revitalization', 'mixed-use']
  },

  // Forensic Templates
  {
    id: 'forensic-scene',
    name: 'Crime Scene Analysis',
    description: 'Document forensic investigation',
    agentId: 'forensic',
    prompt: 'Document a crime scene showing evidence markers, trajectory analysis, blood spatter patterns, and chain of custody procedures.',
    tags: ['crime-scene', 'evidence', 'investigation']
  },

  // Geological Templates
  {
    id: 'geological-formation',
    name: 'Geological Cross-Section',
    description: 'Illustrate Earth\'s layers and formations',
    agentId: 'geological',
    prompt: 'Create a geological cross-section showing sedimentary layers, fault lines, mineral deposits, and groundwater flow in a mountain region.',
    tags: ['geology', 'cross-section', 'formation']
  },

  // Aerospace Templates
  {
    id: 'aerospace-spacecraft',
    name: 'Interplanetary Spacecraft',
    description: 'Design next-generation space vehicles',
    agentId: 'aerospace',
    prompt: 'Design an interplanetary spacecraft with nuclear propulsion, rotating habitat, radiation shielding, and modular cargo sections.',
    tags: ['spacecraft', 'interplanetary', 'advanced']
  },

  // Neurological Templates
  {
    id: 'neuro-brain',
    name: 'Neural Network Mapping',
    description: 'Visualize brain connectivity',
    agentId: 'neurological',
    prompt: 'Map neural pathways in the visual cortex showing information flow from retina through LGN to V1 and higher visual areas.',
    tags: ['brain', 'neural-network', 'visual']
  },

  // Archaeological Templates
  {
    id: 'archaeological-site',
    name: 'Ancient City Reconstruction',
    description: 'Recreate historical settlements',
    agentId: 'archaeological',
    prompt: 'Reconstruct a Bronze Age settlement showing defensive walls, residential areas, temples, workshops, and agricultural fields.',
    tags: ['ancient', 'reconstruction', 'bronze-age']
  },

  // Climate Templates
  {
    id: 'climate-model',
    name: 'Climate Change Visualization',
    description: 'Illustrate climate system dynamics',
    agentId: 'climate',
    prompt: 'Visualize global climate system showing ocean currents, atmospheric circulation, ice sheet dynamics, and carbon cycle feedback loops.',
    tags: ['climate', 'global', 'systems']
  }
];

export const getTemplatesByAgent = (agentId: string): NicheAgentTemplate[] => {
  return NICHE_AGENT_TEMPLATES.filter(template => template.agentId === agentId);
};

export const getTemplatesByTag = (tag: string): NicheAgentTemplate[] => {
  return NICHE_AGENT_TEMPLATES.filter(template => template.tags.includes(tag));
};